import { useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import PageSchema from "@/components/PageSchema";
import LanguageSwitchLink from "@/components/LanguageSwitchLink";
import ogDump from "@/assets/gooseneck-dump.jpg";
import { setPageMeta, setHreflang } from "@/lib/seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const EN_URL = "/dump-trailers";
const ES_URL = "/es/remolques-de-volteo";

export default function RemolquesDeVolteoPage() {
  useEffect(() => {
    document.documentElement.lang = "es";
    setPageMeta(
      "Remolques y Tráileres de Volteo Explicados | Juan The Trailer Guy",
      "Aprende cómo funcionan los tráileres de volteo (dump trailers): GVWR, ejes, altura de pared, tipos de hoist y qué revisar antes de comprar.",
      ES_URL,
      { image: ogDump, type: "article", imageAlt: "Tráiler de volteo resistente con hoist telescópico" },
    );
    const cleanup = setHreflang([
      { lang: "en-US", href: EN_URL },
      { lang: "es-US", href: ES_URL },
      { lang: "x-default", href: EN_URL },
    ]);
    return () => {
      document.documentElement.lang = "en";
      cleanup();
    };
  }, []);

  return (
    <main className="bg-white text-zinc-900">
      <section className="relative isolate overflow-hidden">
        <img src={ogDump} alt="Tráiler de volteo resistente" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 mx-auto max-w-5xl px-6 py-24 md:px-10">
          <PageSchema
            name="Remolques y Tráileres de Volteo Explicados"
            description="Aprende cómo funcionan los tráileres de volteo: GVWR, ejes, altura de pared, tipos de hoist y qué revisar antes de comprar."
          />
          <Breadcrumbs
            variant="dark"
            className="mb-5"
            items={[{ name: "Inicio", href: "/" }, { name: "Guías Educativas" }, { name: "Tráileres de Volteo" }]}
          />
          <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-white/90">
            Tráileres de Volteo
          </span>
          <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white md:text-6xl">
            Remolques y Tráileres de Volteo Explicados: Capacidad, Hoists y Qué Revisar
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-white/80 md:text-lg">
            Una guía en lenguaje sencillo sobre la capacidad de los tráileres de volteo (dump trailers),
            altura de pared, tipos de hoist y paquetes de ejes — para que sepas qué buscar antes de
            comprar, sin importar la marca.
          </p>
          <div className="mt-6">
            <LanguageSwitchLink href={EN_URL} label="Read this guide in English" />
          </div>
        </div>
      </section>

      <section id="guia-volteo" className="mx-auto max-w-7xl px-6 py-16 md:px-10">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">¿Qué es un tráiler de volteo?</p>
            <h2 className="mt-3 text-3xl font-black uppercase leading-tight md:text-4xl">
              Un remolque diseñado para cargar, acarrear y descargar material rápido
            </h2>
          </div>
          <div className="text-lg leading-8 text-zinc-700">
            <p>
              Los tráileres de volteo están hechos para acarrear material suelto, escombro y cargas pesadas
              que necesitan descargarse rápido. Se usan mucho en construcción, techado (roofing), limpieza
              de terrenos, recolección de basura (junk removal), trabajo de rancho, jardinería y trabajos
              generales de contratista.
            </p>
            <p className="mt-4">
              La configuración correcta depende de qué acarreas más seguido, qué tan pesadas son tus
              cargas, cuánto espacio de cama necesitas, y qué tipo de hoist tiene más sentido para tu
              trabajo.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-zinc-50">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-16 md:grid-cols-2 md:px-10">
          <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
            <h3 className="text-2xl font-black uppercase">Capacidad y Paquetes de Ejes</h3>
            <p className="mt-4 leading-7 text-zinc-700">
              Una de las primeras cosas que comparan los compradores es la capacidad de carga. El tamaño
              del remolque, el paquete de ejes y la construcción en general afectan cuánto peso aguanta y
              para qué tipo de trabajo es ideal.
            </p>
            <ul className="mt-6 space-y-3 text-zinc-700">
              <li>• Las configuraciones más ligeras son comunes para trabajos pequeños y presupuestos ajustados</li>
              <li>• Los paquetes de ejes más pesados son mejores para acarreo pesado y repetitivo</li>
              <li>• La capacidad debe ajustarse al uso real diario, no solo a cargas ocasionales</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
            <h3 className="text-2xl font-black uppercase">Altura de Pared y Tamaño de Cama</h3>
            <p className="mt-4 leading-7 text-zinc-700">
              Las dimensiones de la cama afectan cuánto volumen puedes cargar y qué tipo de material cabe
              mejor. Hay que pensar tanto en el peso como en el volumen al elegir el tráiler correcto.
            </p>
            <ul className="mt-6 space-y-3 text-zinc-700">
              <li>• Paredes más altas ayudan con material ligero y voluminoso</li>
              <li>• Paredes más bajas pueden facilitar la carga de equipo (maquinaria)</li>
              <li>• El tamaño de la cama debe ajustarse al tipo de material y al flujo de trabajo</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 md:px-10">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Usos Comunes</p>
          <h2 className="mt-3 text-3xl font-black uppercase md:text-4xl">Dónde encajan mejor los tráileres de volteo</h2>
          <p className="mt-6 text-lg leading-8 text-zinc-700">
            Los tráileres de volteo son populares entre contratistas, jardineros, techadores (roofers),
            rancheros, cuadrillas de limpieza de propiedades y dueños de negocio que acarrean grava,
            tierra, maleza, escombro de demolición y otro material de obra. Son uno de los estilos de
            remolque más versátiles cuando el material necesita descargarse rápido.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { title: "Trabajos de Construcción", text: "Ideal para acarrear tierra, grava, arena, escombro de concreto y material de obra activa." },
            { title: "Limpieza y Escombro", text: "Buena opción para recolección de basura, limpieza de propiedades, retiro de techo (tear-off) y maleza." },
            { title: "Trabajo de Rancho y Terreno", text: "Útil para mover alimento, tierra, material de cercado y mantenimiento general de propiedad." },
          ].map((item) => (
            <div key={item.title} className="rounded-2xl border border-zinc-200 p-6 shadow-sm">
              <h3 className="text-xl font-black uppercase">{item.title}</h3>
              <p className="mt-3 leading-7 text-zinc-700">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Consejos para Compradores</p>
          <h2 className="mt-3 text-3xl font-black uppercase md:text-4xl">Qué pensar antes de comprar un tráiler de volteo</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {[
              "Determina si tus cargas son más de peso, de volumen, o de ambos",
              "Elige un tamaño de remolque que vaya bien con tu troca y tu rutina normal de acarreo",
              "Piensa en la altura de pared según el tipo de material que más mueves",
              "Ajusta el paquete de ejes al trabajo que haces cada semana, no solo ocasionalmente",
              "Considera si necesitas el tráiler solo para material o también para cargar equipo",
              "Compra pensando en trabajo repetible y uso a largo plazo, no solo el precio más bajo",
            ].map((tip) => (
              <div key={tip} className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
                <p className="text-lg font-semibold leading-7 text-zinc-800">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16 md:px-10">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Preguntas Frecuentes</p>
        <h2 className="mt-3 text-3xl font-black uppercase md:text-4xl">Preguntas comunes sobre tráileres de volteo</h2>
        <Accordion type="single" collapsible className="mt-8 w-full">
          <AccordionItem value="14k-vs-16k">
            <AccordionTrigger className="text-left text-lg font-bold">
              ¿14K o 16K en un tráiler de volteo — cuál necesito?
            </AccordionTrigger>
            <AccordionContent className="text-base leading-7 text-zinc-700">
              Un tráiler de 14K es el punto ideal para la mayoría del acarreo semanal de un contratista —
              grava, tierra, maleza, demolición. La 16K tiene sentido si acarreas con frecuencia un skid
              steer, mini excavadora, o cargas cerca del límite. Pasarte de capacidad agrega peso y costo;
              quedarte corto desgasta el tráiler más rápido. Mira nuestra guía completa de{" "}
              <Link to="/es/remolque-volteo-14k-vs-16k" className="font-semibold text-primary underline">
                14K vs 16K
              </Link>
              .
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="hoist">
            <AccordionTrigger className="text-left text-lg font-bold">
              Hoist de tijera (scissor) vs hoist telescópico — ¿cuál es la diferencia?
            </AccordionTrigger>
            <AccordionContent className="text-base leading-7 text-zinc-700">
              Un hoist de tijera es más simple y generalmente más económico, levanta la cama con un brazo
              plegable. Un hoist telescópico se extiende en etapas para un ángulo de descarga más suave y
              parejo, lo cual algunos prefieren para material más pesado o pegajoso.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="gvwr">
            <AccordionTrigger className="text-left text-lg font-bold">
              ¿Cómo sé qué GVWR necesito en realidad?
            </AccordionTrigger>
            <AccordionContent className="text-base leading-7 text-zinc-700">
              Empieza con la carga más pesada que acarreas normalmente, no con la más ligera. Agrega un
              margen de seguridad y revisa las capacidades de tu troca. Nuestra{" "}
              <Link to="/es/gvwr-capacidad-de-carga" className="font-semibold text-primary underline">
                guía de GVWR y capacidad de carga
              </Link>{" "}
              explica el cálculo paso a paso.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl md:text-5xl font-black uppercase">Sigue Aprendiendo</h2>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/es/remolque-volteo-14k-vs-16k" className="bg-red-700 px-6 py-3 rounded-md font-bold uppercase hover:bg-red-800">
              Tráiler de Volteo 14K vs 16K
            </Link>
            <Link to="/es/costos-de-tener-un-remolque" className="border border-white/30 px-6 py-3 rounded-md font-bold uppercase hover:bg-white/10">
              Costos de Tener un Remolque
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
